import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { calcAgeFromDob, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { requiresCustodialAuthority } from './attorneyGeneralHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;
const MAX_DESC = 2000;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string; code?: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string; code?: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function AttorneyGeneralStep6AccusedPersonPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useAttorneyGeneralLookups('step6');

  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const age = useMemo(() => calcAgeFromDob(draft.accusedDob), [draft.accusedDob]);
  const custodyStatusOptions = useMemo(
    () => mergeLookupItems(lookups.custodyStatuses, draft.custodyStatusId, draft.custodyStatusName),
    [draft.custodyStatusId, draft.custodyStatusName, lookups.custodyStatuses],
  );
  const selectedCustodyStatus = useMemo(
    () => custodyStatusOptions.find((s) => s.id === draft.custodyStatusId),
    [custodyStatusOptions, draft.custodyStatusId],
  );
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustodyStatus?.code,
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(6, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={6} draft={draft}>
      <SectionCard title={f.sectionAccusedPersonDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.accusedName}</label>
          <input
            id="ag-accusedName"
            className={inpClass(Boolean(errors.accusedName))}
            type="text"
            value={draft.accusedName}
            onChange={(e) => {
              setDraft((d) => ({ ...d, accusedName: maxLen('accusedName', e.target.value, MAX_NAME) }));
            }}
            placeholder={f.enterAccusedName}
          />
          <FieldError message={errors.accusedName} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.nationality}</label>
            <WizardSelect
              id="ag-nationality"
              hasError={Boolean(errors.nationalityId)}
              value={draft.nationalityId}
              disabled={lookups.loading}
              placeholder={lookups.loading ? 'Loading…' : f.selectNationality}
              options={toOpts(mergeLookupItems(lookups.nationalities, draft.nationalityId, draft.nationalityName))}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  nationalityId: id,
                  nationalityName: lookupName(lookups.nationalities, id) || d.nationalityName }));
                clearErr('nationalityId');
              }}
            />
            <FieldError message={errors.nationalityId} />
          </div>
          <div className="ccr-fg">
            <label>{f.countryOfOrigin}</label>
            <WizardSelect
              id="ag-countryOfOrigin"
              hasError={Boolean(errors.countryOfOriginId)}
              value={draft.countryOfOriginId}
              disabled={lookups.loading}
              placeholder={f.selectCountryOfOrigin}
              options={toOpts(lookups.countries)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  countryOfOriginId: id,
                  countryOfOriginName: lookupName(lookups.countries, id) }));
                clearErr('countryOfOriginId');
              }}
            />
            <FieldError message={errors.countryOfOriginId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.dateOfBirth}</label>
            <FormattedDateInput
              id="ag-accusedDob"
              className={inpClass(Boolean(errors.accusedDob))}
              value={draft.accusedDob}
              maxDate={maxDob}
              enableYmdViews
              placeholder={f.dateOfBirth}
              onChange={(next) => {
                setDraft((d) => ({ ...d, accusedDob: next }));
                clearErr('accusedDob');
              }}
            />
            <FieldError message={errors.accusedDob} />
          </div>
          <div className="ccr-fg">
            <label>{f.age}</label>
            <input className="ccr-inp" type="text" value={typeof age === 'number' ? String(age) : ''} readOnly disabled />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.gender}</label>
            <WizardSelect
              id="ag-accusedGender"
              hasError={Boolean(errors.accusedGenderId)}
              value={draft.accusedGenderId}
              disabled={lookups.loading}
              placeholder={f.selectGender}
              options={toOpts(lookups.genders)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  accusedGenderId: id,
                  accusedGenderName: lookupName(lookups.genders, id) }));
                clearErr('accusedGenderId');
              }}
            />
            <FieldError message={errors.accusedGenderId} />
          </div>
          <div className="ccr-fg">
            <label>{f.passportIdNumber}</label>
            <input
              id="ag-passport"
              className={inpClass(Boolean(errors.passportOrIdNumber))}
              type="text"
              value={draft.passportOrIdNumber}
              onChange={(e) => {
                setDraft((d) => ({ ...d, passportOrIdNumber: maxLen('passportOrIdNumber', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Passport/ID Number')}
            />
            <FieldError message={errors.passportOrIdNumber} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.currentCustodyStatus}</label>
            <WizardSelect
              id="ag-custodyStatus"
              hasError={Boolean(errors.custodyStatusId)}
              value={draft.custodyStatusId}
              disabled={lookups.loading}
              placeholder={f.selectCustodyStatus}
              options={toOpts(custodyStatusOptions)}
              onChange={(id) => {
                const item = custodyStatusOptions.find((x) => x.id === id);
                const name = item?.name ?? lookupName(custodyStatusOptions, id);
                const code = item?.code ?? '';
                setDraft((d) => ({
                  ...d,
                  custodyStatusId: id,
                  custodyStatusName: name,
                  ...(!requiresCustodialAuthority(name, code) ? { custodialAuthority: '' } : {}) }));
                clearErr('custodyStatusId');
              }}
            />
            <FieldError message={errors.custodyStatusId} />
          </div>
          {showCustodialAuthority ? (
            <div className="ccr-fg">
              <label>{f.custodialAuthority}</label>
              <input
                id="ag-custodialAuthority"
                className={inpClass(Boolean(errors.custodialAuthority))}
                type="text"
                value={draft.custodialAuthority}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, custodialAuthority: maxLen('custodialAuthority', e.target.value, MAX_NAME) }));
                }}
                placeholder={f.custodialAuthorityHint}
              />
              <FieldError message={errors.custodialAuthority} />
            </div>
          ) : null}
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.lastKnownLocation}</label>
          <textarea
            id="ag-lastKnownLocation"
            className={inpClass(Boolean(errors.lastKnownLocation))}
            rows={3}
            value={draft.lastKnownLocation}
            onChange={(e) => {
              setDraft((d) => ({ ...d, lastKnownLocation: maxLen('lastKnownLocation', e.target.value, MAX_DESC) }));
            }}
            placeholder={agEnterPlaceholder('Last Known Location')}
          />
          <FieldError message={errors.lastKnownLocation} />
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={6}
        onBack={() => nav(attorneyGeneralWizardPath(5, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(7, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
